import { Injectable } from '@angular/core';
import { InspectionPlan } from 'app/viewmodels/analysis/inspection-plan';
import { InspectionPlanStatus } from 'app/viewmodels/analysis/inspection-plan-status.enum';
import { InspectionPlanTask } from 'app/viewmodels/analysis/inspection-plan-task';
import { InspectionPlanTaskMitigationColumn } from 'app/viewmodels/analysis/inspection-plan-task-mitigation-column';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuditEntityService } from '../../core/audit-entity.service';
import { BackendService } from '../../core/backend.service';
import { CONFIG } from '../../core/config';
import { StaticLookupService } from '../../core/static-lookups/static-lookup.service';
import { ErpMitigationJob } from '../../viewmodels/admin/integration/erp-mitigation-job';
import { ErpMitigationJobs } from '../../viewmodels/admin/integration/erp-mitigation-jobs';
import { MitigationPlanTaskSource } from '../../viewmodels/analysis/inspection-plan-task-source';
import { LastInspectionDateSource } from '../../viewmodels/analysis/last-inspection-date-source';
import { MitigationPlanFrequencySource } from '../../viewmodels/analysis/mitigation-plan-frequency-source';
import { Recommendation } from '../../viewmodels/analysis/recommendation';
import { Recommendations } from '../../viewmodels/analysis/recommendations';

@Injectable({
  providedIn: 'root',
})
export class MitigationPlanService {
  /**
   * The currently active/displayed Mitigation Plan.
   */
  private _currentMitigationPlan: Recommendations;

  /**
   * Setter for the private field, used largely for testability.
   * @param {Recommendations} The value to set the private field to.
   */
  set currentMitigationPlan(value: Recommendations) {
    this._currentMitigationPlan = value;
  }

  /**
   * Getter for private field, used so we can access the required data in the template.
   * @returns {Recommendations} The value of the private field.
   */
  get currentMitigationPlan(): Recommendations {
    return this._currentMitigationPlan;
  }

  /**
   * The orgTreeNodeId for the Asset that the current Mitigation Plan is for.
   * Used to get and reload the Mitigation Plan.
   */
  private _orgTreeNodeId: string;

  get orgTreeNodeId(): string {
    return this._orgTreeNodeId;
  }

  /**
   * List of Mitigation Jobs retreived from the API. Used to populate the dialog shown
   * in the component.
   */
  public erpMaintenanceJobs: ErpMitigationJobs;

  /**
   * The currently selected job. Highlighted in the list in the dialog.
   */
  public selectedErpJob: ErpMitigationJob;

  /**
   * Used to propagate the dropdown of Inspection Priorities when creating a new Ad-Hoc Task.
   */
  public inspectionPriorities: string[];

  public erpIdentifier: string;
  public erpEnabledTaskCount: number;

  inspectionPlanStatusEnum = InspectionPlanStatus;

  public prevInspectionPlanTasks: Recommendation[] = [];

  public mitigationPlanIsLoaded = new Subject();

  constructor(
    private backendService: BackendService,
    private auditEntityService: AuditEntityService,
    private staticLookupService: StaticLookupService,
  ) {}

  /**
   * Sets the value of currentMitigationPlan based on an API call using orgTreeNodeId.
   * @param {string} orgTreeNodeId Identifies the associated Asset.
   * @param {string} inspectionPlanId Used to load a specific inspection plan by ID.
   * @param {InspectionPlanStatus} inspectionPlanStatus Plan status used to swap between Active and Draft plans.
   */
  public loadMitigationPlan(orgTreeNodeId: string, inspectionPlanId?: string, inspectionPlanStatus?: InspectionPlanStatus): void {
    this._orgTreeNodeId = orgTreeNodeId;
    const idsArray = [orgTreeNodeId];
    if (inspectionPlanId) {
      idsArray.push(inspectionPlanId);
    }
    if (inspectionPlanStatus) {
      idsArray.push(inspectionPlanStatus.toString());
    }
    this.backendService
      .getByIdArrayRoute<Recommendations>(Recommendations, idsArray, CONFIG.apiRoutes.currentRbiInspectionPlanByOrgTreeNode)
      .subscribe((res: Recommendations) => {
        this.setMitigationPlanWhenLoaded(res);
        this.mitigationPlanIsLoaded.next();
      });
  }

  public getPreviousInspectionPlanTasks(currentInspectionPlanId: string): void {
    this.backendService
      .getAllById<InspectionPlanTask>(Recommendation, currentInspectionPlanId, CONFIG.apiRoutes.previousInspectionPlanTasks)
      .subscribe((prevInspectionPlanTasks: Recommendation[]) => {
        if (prevInspectionPlanTasks) {
          this.prevInspectionPlanTasks = prevInspectionPlanTasks;
          this.comparePrevPlanTasksWithCurrentPlanTasks(prevInspectionPlanTasks);
        }
      });
  }

  /**
   * Adds a new Mitigation Plan Task to the currently-active Mitigation Plan.
   * @param {Recommendation} The task to add to the plan.
   */
  public createMitigationPlanTask(newTask: Recommendation): void {
    this.auditEntityService.auditNew(newTask);
    newTask.lastInspectionDateSource = LastInspectionDateSource.InspectionReport;
    newTask.frequencySource = MitigationPlanFrequencySource.RecommendationFrequency;
    this.setInspectionPlanTaskMitigationColumns(newTask);

    if (this.isCurrentMitigationPlanActive()) {
      this.addCurrentPlanTask(newTask);
      this.createMitigationPlanFromCurrent();
    } else {
      this.backendService.create<InspectionPlanTask>(InspectionPlanTask, newTask).subscribe((data: Recommendation) => {
        newTask.inspectionPlanTaskId = data.inspectionPlanTaskId;
        newTask.setPermissions();

        if (newTask.sourceType === MitigationPlanTaskSource.AdHoc) {
          this.currentMitigationPlan.items.push(newTask);
        }

        if (this.currentMitigationPlan) {
          this.loadMitigationPlan(this._orgTreeNodeId, this.currentMitigationPlan.inspectionPlanId);
        }
      });
    }
  }

  private setInspectionPlanTaskMitigationColumns(newTask: InspectionPlanTask): void {
    if (newTask.sourceType === MitigationPlanTaskSource.AdHoc) {
      this.createAdHocMitigationColumns(newTask);
    } else {
      this.createRecommendationMitigationColumns(newTask);
    }
  }

  private createAdHocMitigationColumns(newTask: InspectionPlanTask): void {
    const mitigationColumns: InspectionPlanTaskMitigationColumn[] = [];
    if (this.currentMitigationPlan.mitigationColumns) {
      this.currentMitigationPlan.mitigationColumns.forEach(e => {
        mitigationColumns.push(this.createInspectionPlanTaskMitigationColumn('', e.mitigationColumnId, newTask.inspectionPlanTaskId));
      });
    }
    newTask.mitigationColumns = mitigationColumns;
  }

  private createRecommendationMitigationColumns(newTask: InspectionPlanTask): void {
    if (newTask.displayedMitigationColumns) {
      const displayedMitigationColumns = JSON.parse(newTask.displayedMitigationColumns);
      if (displayedMitigationColumns && displayedMitigationColumns.length > 0) {
        newTask.mitigationColumns = displayedMitigationColumns.map(e =>
          this.createInspectionPlanTaskMitigationColumn(e.CellText, e.ColumnId, newTask.inspectionPlanTaskId),
        );
      }
    }
  }

  private createInspectionPlanTaskMitigationColumn(
    cellText: string,
    mitigationColumnId: string,
    inspectionPlanTaskId: string,
  ): InspectionPlanTaskMitigationColumn {
    const mitigationColumn = new InspectionPlanTaskMitigationColumn();
    mitigationColumn.cellText = cellText;
    mitigationColumn.mitigationColumnId = mitigationColumnId;
    mitigationColumn.inspectionPlanTaskId = inspectionPlanTaskId;
    return mitigationColumn;
  }

  /**
   * Used to save task details when editing an existing task.
   * @param {Recommendation} item The task whose details have been edited.
   * @param {boolean} loadMitigationPlan Whether or not to reload the current Mitigation Plan after saving.
   */
  public saveTask(task: Recommendation, loadMitigationPlan: boolean): void {
    this.auditEntityService.auditExisting(task);

    if (this.isCurrentMitigationPlanActive()) {
      // if triggered by sendToErp, only one task may have sendToErp enabled
      this.disableSendErpForAllApartCurrent(task);
      this.updateCurrentPlanTask(task);
      this.createMitigationPlanFromCurrent();
    } else {
      this.backendService.update<InspectionPlanTask>(InspectionPlanTask, task.inspectionPlanTaskId, task).subscribe(() => {
        if (loadMitigationPlan) {
          this.loadMitigationPlan(this._orgTreeNodeId, this.currentMitigationPlan.inspectionPlanId);
        }
      });
    }
  }

  /**
   * Deletes a given task from the current mitigation plan.
   * @param {Recommendation} task The task to delete.
   */
  public deleteTask(task: Recommendation): void {
    if (this.isCurrentMitigationPlanActive()) {
      this.deleteCurrentPlanTask(task);
      this.createMitigationPlanFromCurrent();
    } else {
      this.backendService.delete<InspectionPlanTask>(InspectionPlanTask, task.inspectionPlanTaskId).subscribe(() => {
        this.loadMitigationPlan(this._orgTreeNodeId, this.currentMitigationPlan.inspectionPlanId);
      });
    }
  }

  /**
   * Makes an API call to retreive the ERP Job Plan for the current Mitigation Plan.
   * @param {Recommendation} task The task that we're using for the query. Required
   * to set one of the ERP fields after the API call completes.
   */
  public getMitigationJobs(task: Recommendation): Observable<void> {
    return this.backendService.getById<ErpMitigationJobs>(ErpMitigationJobs, this._orgTreeNodeId).pipe(
      map(erpMaintenanceJobs => {
        if (erpMaintenanceJobs) {
          this.erpMaintenanceJobs = erpMaintenanceJobs;
          this.selectedErpJob = null;
        }
      }),
    );
  }

  public comparePrevPlanTasksWithCurrentPlanTasks(prevInspectionPlanTasks: Recommendation[]): void {
    const { items } = this.currentMitigationPlan;
    items.forEach(currentTask => {
      const previousTask = prevInspectionPlanTasks.find(
        x =>
          x.taskReference.toUpperCase() === currentTask.taskReference.toUpperCase() &&
          x.taskName.toUpperCase() === currentTask.taskName.toUpperCase(),
      );

      if (!previousTask) {
        currentTask.flagRow = true;
      } else {
        currentTask.flagRow = false;
        currentTask.flagtaskInterval = previousTask.taskInterval !== currentTask.taskInterval;
        currentTask.flagtaskFrequency = previousTask.taskFrequency !== currentTask.taskFrequency;
        currentTask.flagtaskExtent = previousTask.taskExtent !== currentTask.taskExtent;
        currentTask.flagmitigationType = previousTask.mitigationType !== currentTask.mitigationType;
        currentTask.flagtaskName = previousTask.taskName !== currentTask.taskName;
      }
    });
  }

  /**
   * Makes a call to the lookup service to populate the dropdown list options.
   */
  public getInspectionPriorities(): void {
    this.inspectionPriorities = [];
    // Note: The type is 'MitigationType' for historical reasons, InspectionPriority is the correct current parlance
    this.staticLookupService.getByIdAsync('MitigationType').subscribe(data => {
      data.forEach(element => {
        this.inspectionPriorities.push(element.value);
      });
    });
  }

  /**
   * Activates the current mitigation plan. Makes a call to the backend service.
   */
  public activateMitigationPlan(): void {
    this.backendService.update<InspectionPlan>(InspectionPlan, this.currentMitigationPlan.inspectionPlanId, new InspectionPlan()).subscribe(() => {
      this.loadMitigationPlan(this._orgTreeNodeId);
    });
  }

  private isCurrentMitigationPlanActive(): boolean {
    return this._currentMitigationPlan.inspectionPlanStatus === this.inspectionPlanStatusEnum.Active;
  }

  public createMitigationPlanFromCurrent(): void {
    this.backendService
      .postObjectRoute<Recommendations>(CONFIG.apiRoutes.createInspectionPlanFromCurrent, this._currentMitigationPlan, true)
      .subscribe((res: Recommendations) => {
        if (res) {
          const recommendations = new Recommendations();
          recommendations.loadFromJson(res);
          this.setMitigationPlanWhenLoaded(recommendations);
        }
      });
  }

  private updateCurrentPlanTask(task: Recommendation): void {
    const { items } = this._currentMitigationPlan;
    const index = this.getCurrentPlanTaskIndex(task);
    if (index > -1) {
      items[index] = task;
    }
  }

  private deleteCurrentPlanTask(task: Recommendation): void {
    const { items } = this._currentMitigationPlan;
    const index = this.getCurrentPlanTaskIndex(task);
    if (index > -1) {
      items.splice(index, 1);
    }
  }

  private addCurrentPlanTask(task: Recommendation): void {
    const { items } = this._currentMitigationPlan;
    items.push(task);
  }

  private getCurrentPlanTaskIndex(task: Recommendation): number {
    return this._currentMitigationPlan.items.indexOf(
      this._currentMitigationPlan.items.find(e => e.inspectionPlanTaskId === task.inspectionPlanTaskId),
    );
  }

  private setMitigationPlanWhenLoaded(res: Recommendations): void {
    if (res) {
      this.currentMitigationPlan = res;
      this.getPreviousInspectionPlanTasks(res.inspectionPlanId);
      this.erpEnabledTaskCount = this.currentMitigationPlan.items.filter(x => x.sendToErp === true).length;
    }
  }

  private disableSendErpForAllApartCurrent(task: Recommendation): void {
    if (task.sendToErp) {
      const { items } = this._currentMitigationPlan;
      items.forEach(e => {
        if (e.inspectionPlanTaskId !== task.inspectionPlanTaskId) {
          e.sendToErp = false;
        }
      });
    }
  }

  public savePlanTaskMitigationColumn(planTaskMitigationColumn: InspectionPlanTaskMitigationColumn): void {
    if (this.isCurrentMitigationPlanActive()) {
      this.createMitigationPlanFromCurrent();
    } else {
      this.backendService
        .update<InspectionPlanTaskMitigationColumn>(
        InspectionPlanTaskMitigationColumn,
        planTaskMitigationColumn.inspectionPlanTaskMitigationColumnId,
        planTaskMitigationColumn,
        false,
      )
        .subscribe(() => {});
    }
  }
}
